import React, { useState, useEffect, useMemo, useRef } from "react";
import { Modal, Input, Row, Col, Button, message } from "antd";
import { CloseOutlined, PlusOutlined, MinusOutlined } from "@ant-design/icons";
import DeleteIcon from "../../assets/images/delete1.svg";
import Refresh from "../../assets/images/refresh.svg";
import CancelArrow from "../../assets/images/cancelArrow.svg";
import { v4 as uuidv4 } from "uuid";
import BarCodeHandler from "../BarcodeHandler";
import { Scrollbars } from "react-custom-scrollbars";
import Axios from "axios";
import { stock } from "../../../src/components/PointOfSale/Core/stock.js";
import messages from "../../errors/messages.en-IN.json";
import { getApplicablePrice } from "../../utility/getPriceList.js";
import db from "../../database";

const ProductsPopup = (props) => {
  const {
    productData,
    visibleProductsModal,
    setVisibleProductsModal,
    selectedItems,
    setSelectedItems,
    onSubmit,
    selectedProduct,
    setSelectedProduct,
    selectedProductCallback,
    weightPopup,
    setWeightPopup,
    selectedProductInCart,
    setSelectedProductInCart,
    cart,
    setCart,
    setProductStock,
  } = props;

  const posConfig = JSON.parse(localStorage.getItem("posConfig"));
  const itemsPerPage = 10;

  const tillData = JSON.parse(localStorage.getItem("tillData"));
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showProductSelection, setShowProductSelection] = React.useState(false);
  const [isFirstInput, setIsFirstInput] = useState(true);
  const [unit, setUnit] = useState("kg");
  const [hasDecimal, setHasDecimal] = useState(false);
  const weightInputRef = useRef(null);

  const [uomPopup, setUomPopup] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [selectedUomId, setSelectedUomId] = useState(null);
  const [uomQuantity, setUomQuantity] = useState("1");
  const uomInputRef = useRef(null);
  const uomConfirmedRef = useRef(false);

  useEffect(() => {
    if (uomConfirmedRef.current) {
      uomConfirmedRef.current = false;
      return;
    }

    if (selectedProduct?.alternateUom?.length > 0 && Array.isArray(selectedProduct?.alternateUom) && selectedProduct?.showAlternateUomInPos === "Y") {
      setWeightPopup(false);
      setPendingProduct(selectedProduct); // This was missing - needed for availableUoms
      setUomPopup(true);
    }
  }, [selectedProduct]);

  const availableUoms = useMemo(() => {
    if (!pendingProduct) return [];

    const uoms = [];
    const addedUomIds = new Set(); // Track added UOM IDs to prevent duplicates

    // Add primary UOM from uomData
    if (pendingProduct.uomData && pendingProduct.uomData.length > 0) {
      const primaryUom = pendingProduct.uomData[0];
      uoms.push({
        csUomId: primaryUom.csUomId,
        name: primaryUom.name,
        ediCode: primaryUom.ediCode || primaryUom.symbol,
        isPrimary: true,
        decimal: primaryUom.decimal,
        stdprecision: primaryUom.stdprecision,
        convertionRate: 1,
      });
      addedUomIds.add(primaryUom.csUomId); // Mark primary UOM as added
    }

    // Add alternate UOMs (only if not already added)
    if (pendingProduct.alternateUom && pendingProduct.alternateUom.length > 0) {
      pendingProduct.alternateUom.forEach((altUom) => {
        if (addedUomIds.has(altUom.csUomId)) {
          return;
        }
        uoms.push({
          csUomId: altUom.csUomId,
          name: altUom.name,
          ediCode: altUom.ediCode,
          isPrimary: false,
          convertionRate: altUom.convertionRate,
          decimal: "N",
          stdprecision: 0,
        });
        addedUomIds.add(altUom.csUomId); // Mark alternate UOM as added
      });
    }

    return uoms;
  }, [pendingProduct]);

  const selectedUom = useMemo(() => {
    return availableUoms.find((uom) => uom.csUomId === selectedUomId);
  }, [availableUoms, selectedUomId]);

  const uomPrice = useMemo(() => {
    if (!pendingProduct || !selectedUomId) {
      return pendingProduct?.sunitprice || 0;
    }

    // First try getApplicablePrice if sPriceListId is available
    if (cart?.customer?.sPriceListId) {
      const priceData = getApplicablePrice({
        priceList: pendingProduct.priceList,
        selectedUomId: selectedUomId,
        sPriceListId: cart?.customer?.sPriceListId,
      });

      if (priceData) {
        return priceData.pricestd || priceData.pricelist || pendingProduct.sunitprice || 0;
      }
    }

    // Fallback: Find price directly by uomId when sPriceListId is undefined
    if (pendingProduct.priceList && pendingProduct.priceList.length > 0) {
      const uomPrice = pendingProduct.priceList.find((p) => p.uomId === selectedUomId);
      if (uomPrice) {
        return uomPrice.pricestd || uomPrice.pricelist || pendingProduct.sunitprice || 0;
      }
    }

    // No UOM-specific price found, use default price
    return pendingProduct.sunitprice || 0;
  }, [pendingProduct, selectedUomId, cart?.customer?.sPriceListId]);

  const uomTotalPrice = useMemo(() => {
    const qty = Number.parseFloat(uomQuantity) || 0;
    return (uomPrice * qty).toFixed(2);
  }, [uomPrice, uomQuantity]);

  useEffect(() => {
    if (uomPopup && pendingProduct) {
      const primaryUomId = pendingProduct.uom || pendingProduct.uomData?.[0]?.csUomId;
      setSelectedUomId(primaryUomId);
      setUomQuantity("1");
      setIsFirstInput(true);
    }
  }, [uomPopup, pendingProduct]);

  useEffect(() => {
    if (uomPopup && uomInputRef.current) {
      setTimeout(() => {
        uomInputRef.current?.focus();
        uomInputRef.current?.select();
      }, 100);
    }
  }, [uomPopup]);

  const handleUomInputChange = (value) => {
    const isDecimal = selectedUom?.decimal === "Y";
    let sanitizedValue = value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");

    if (!isDecimal) {
      sanitizedValue = sanitizedValue.replace(/\./g, "");
    }

    setUomQuantity(sanitizedValue === "" ? "0" : sanitizedValue);
    setIsFirstInput(false);
  };

  const handleUomConfirm = async () => {
    const qty = Number.parseFloat(uomQuantity) || 1;

    console.log("[v0] handleUomConfirm called");
    console.log("[v0] selectedUomId:", selectedUomId);
    console.log("[v0] selectedUom:", selectedUom);
    console.log("[v0] uomPrice from useMemo:", uomPrice);
    console.log("[v0] uomQuantity:", uomQuantity);
    console.log("[v0] qty parsed:", qty);
    console.log("[v0] pendingProduct.sunitprice:", pendingProduct?.sunitprice);

    if (qty <= 0) {
      message.error("Quantity must be greater than 0");
      return;
    }

    // Fetch uomData from db.productUom using the selected UOM's csUomId
    let newUomData = pendingProduct.uomData;

    if (selectedUomId) {
      try {
        const uomFromDb = await db.productUom.where("csUomId").equals(selectedUomId).toArray();
        console.log("[v0] uomFromDb result:", uomFromDb);

        if (uomFromDb && uomFromDb.length > 0) {
          newUomData = uomFromDb;
        }
      } catch (error) {
        console.error("Error fetching uomData from db:", error);
        // Fallback to original uomData if db query fails
        newUomData = pendingProduct.uomData;
      }
    }

    console.log("[v0] newUomData:", newUomData);

    // Build updated product with new UOM, uomData and price - same pattern as weight popup confirm
    const updatedProduct = {
      ...pendingProduct,
      uom: selectedUomId,
      uomData: newUomData,
      weight: qty,
      rawWeight: uomQuantity,
      unit: selectedUom?.name?.toLowerCase() || pendingProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
      sunitprice: uomPrice,
      price: uomPrice,
      salePrice: uomPrice,
      realPrice: uomPrice,
    };

    console.log("[v0] updatedProduct being sent:", updatedProduct);
    console.log("[v0] updatedProduct prices - sunitprice:", updatedProduct.sunitprice, "price:", updatedProduct.price);

    uomConfirmedRef.current = true;

    // Same flow as weight popup confirm button
    setSelectedProduct(updatedProduct);
    setUomPopup(false);
    setPendingProduct(null);
    setSelectedUomId(null);
    setUomQuantity("1");
    selectedProductCallback(updatedProduct);
  };

  const handleUomCancel = () => {
    resetModalState();
  };

  const getUomIcon = (uomName) => {
    const name = uomName?.toLowerCase() || "";
    if (name.includes("piece") || name.includes("pcs") || name.includes("nos") || name.includes("unit")) {
      return (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
          <line x1="12" y1="22.08" x2="12" y2="12" />
        </svg>
      );
    }
    // Default: weight/kg icon
    return (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 3v18" />
        <path d="M8 6l4-3 4 3" />
        <rect x="4" y="10" width="16" height="10" rx="1" />
        <path d="M8 15h8" />
        <path d="M8 18h8" />
      </svg>
    );
  };

  // Filter and sort products
  useEffect(() => {
    let filtered = productData.filter((item) => {
      const lowerQuery = searchQuery.toLowerCase();
      const isValidSegment = item.productSegment !== "CB";
      const matchesSearch =
        item.name.toLowerCase().includes(lowerQuery) || item.value.toLowerCase().includes(lowerQuery) || (item.upc && item.upc.toLowerCase().includes(lowerQuery));
      return isValidSegment && matchesSearch;
    });

    // filtered.sort((a, b) => {
    //   const aInCart = selectedItems.find((item) => item.mProductId === a.mProductId);
    //   const bInCart = selectedItems.find((item) => item.mProductId === b.mProductId);
    //   const aQty = aInCart ? aInCart.weight : 0;
    //   const bQty = bInCart ? bInCart.weight : 0;
    //   return (bQty > 1) - (aQty > 1);
    // });

    setFilteredProducts(filtered);
  }, [searchQuery, productData, selectedItems]);

  const isInitialOpen = useRef(true); // Track initial modal opening

  // Handle focus and selection only on initial open
  useEffect(() => {
    const inputEl = weightInputRef.current;

    if (!weightPopup || !inputEl) {
      isInitialOpen.current = true;
      return;
    }

    // Focus on first open
    if (isInitialOpen.current) {
      inputEl.focus();
      setTimeout(() => {
        inputEl.select();
      }, 50);
      isInitialOpen.current = false;
    }

    // Periodic check to restore focus if lost (e.g., due to re-render or user click outside)
    const intervalId = setInterval(() => {
      if (document.activeElement !== inputEl) {
        inputEl.focus();
      }
    }, 100);

    // Clean up when popup closes
    return () => {
      clearInterval(intervalId);
      isInitialOpen.current = true;
    };
  }, [weightPopup]);

  // Handle state initialization separately
  useEffect(() => {
    if (weightPopup) {
      setIsFirstInput(true);
      setHasDecimal(false);
      // Initialize weight if undefined
      if (!selectedProduct.weight) {
        setSelectedProduct({
          ...selectedProduct,
          weight: "0",
          rawWeight: "0",
        });
      }
      // Set unit based on product data or default to kg when predefined buttons are enabled
      if (posConfig?.predefined_weight_buttons === "Y" && !unit) {
        setUnit(selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg");
      }
    }
  }, [weightPopup, selectedProduct]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredProducts.slice(startIndex, endIndex);
  }, [filteredProducts, currentPage]);

  // Handle page navigation
  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  const handleIncrease = async (product) => {
    const isInCart = selectedItems.find((item) => item.mProductId === product.mProductId);
    const prevSelectedItems = [...selectedItems];
    const isDecimal = product?.uomData?.[0]?.decimal === "Y";
    const updatedSelectedItems = isInCart
      ? selectedItems.map((item) =>
          item.mProductId === product.mProductId
            ? {
                ...item,
                weight: Number(item.weight) + 1,
                rawWeight: (Number(item.weight) + 1).toString(),
                unit: unit || product?.uomData?.[0]?.name?.toLowerCase() || "kg",
              }
            : item
        )
      : [
          ...selectedItems,
          {
            ...product,
            weight: 1,
            rawWeight: "1",
            unit: unit || product?.uomData?.[0]?.name?.toLowerCase() || "kg",
            lineId: uuidv4().replace(/-/g, "").toUpperCase(),
          },
        ];
    const tempCart = {
      ...cart,
      items: updatedSelectedItems,
    };
    const isPreOrderPage = window.location.pathname.includes("/preorder");
    if (navigator.onLine && tillData?.tillAccess?.csBunit?.allowNegativeStock !== "Y" && !isPreOrderPage) {
      let isFinalValidation = false;
      const stockCheckResult = await stock({
        cart: tempCart,
        setCart,
        tillData,
        setProductStock,
        isFinalValidation,
      });

      if (!stockCheckResult) {
        setSelectedItems(prevSelectedItems);
        return;
      }
    }
    setSelectedItems(updatedSelectedItems);
  };

  const handleDecrease = (product) => {
    const isInCart = selectedItems.find((item) => item.mProductId === product.mProductId);
    if (isInCart && isInCart.weight > 1) {
      setSelectedItems(
        selectedItems.map((item) =>
          item.mProductId === product.mProductId
            ? {
                ...item,
                weight: Number(item.weight) - 1,
                rawWeight: (Number(item.weight) - 1).toString(),
                unit: item.unit || unit || product?.uomData?.[0]?.name?.toLowerCase() || "kg",
              }
            : item
        )
      );
    } else if (isInCart && isInCart.weight <= 1) {
      setSelectedItems(selectedItems.filter((item) => item.mProductId !== product.mProductId));
    }
  };

  const handleQuantityChange = async (product, value) => {
    const isDecimal = product?.uomData?.[0]?.decimal === "Y";
    let newValue = value;

    if (newValue === "") {
      newValue = "0";
    }

    const currentWeight = product.rawWeight || (product.weight !== undefined ? product.weight.toString() : "0");
    const isInitialZero = currentWeight === "0";
    if (isInitialZero && /\d/.test(newValue) && !newValue.startsWith("0.")) {
      newValue = newValue.replace(/^0+/, "");
      if (newValue === "") newValue = "0";
    }

    const isValidInput = isDecimal ? /^(\d*\.?\d{0,3}|\d*\.|\d*\.\d{0,3}\.)$/.test(newValue) : /^\d*$/.test(newValue);
    if (!isValidInput) {
      return;
    }

    const parsedValue = newValue === "0" || newValue === "." || newValue === "0." || newValue === "0.0" || newValue === "0.00" ? 0 : parseFloat(newValue) || 0;

    if (parsedValue < 0) {
      message.error(`${messages.info.quantity_not_negative}`);
      return;
    }

    const isInCart = selectedItems.find((item) => item.mProductId === product.mProductId);
    const prevSelectedItems = [...selectedItems];
    let updatedSelectedItems = [];
    if (isInCart) {
      if (parsedValue === 0 && ![".", "0.", "0.0", "0.00"].includes(newValue)) {
        // setSelectedItems(selectedItems.filter((item) => item.mProductId !== product.mProductId));
        updatedSelectedItems = selectedItems.filter((item) => item.mProductId !== product.mProductId);
      } else {
        updatedSelectedItems = selectedItems.map((item) =>
          item.mProductId === product.mProductId
            ? {
                ...item,
                weight: isDecimal ? parsedValue : Math.floor(parsedValue),
                rawWeight: newValue,
                unit: item.unit || unit || product?.uomData?.[0]?.name?.toLowerCase() || "kg",
              }
            : item
        );
      }
    } else if (parsedValue > 0 || [".", "0.", "0.0", "0.00"].includes(newValue)) {
      updatedSelectedItems = [
        ...selectedItems,
        {
          ...product,
          weight: isDecimal ? parsedValue : Math.floor(parsedValue),
          rawWeight: newValue,
          unit: unit || product?.uomData?.[0]?.name?.toLowerCase() || "kg",
          lineId: uuidv4().replace(/-/g, "").toUpperCase(),
        },
      ];
    } else {
      return;
    }
    const tempCart = {
      ...cart,
      items: updatedSelectedItems,
    };
    const isPreOrderPage = window.location.pathname.includes("/preorder");
    if (navigator.onLine && tillData?.tillAccess?.csBunit?.allowNegativeStock !== "Y" && !isPreOrderPage) {
      const stockCheckResult = await stock({
        cart: tempCart,
        setCart,
        tillData,
        setProductStock,
        isFinalValidation: false,
      });
      if (!stockCheckResult) {
        setSelectedItems(prevSelectedItems);
        return;
      }
    }
    setSelectedItems(updatedSelectedItems);
  };

  const handleDelete = (itemToDelete) => {
    setSelectedItems(selectedItems.filter((item) => item.mProductId !== itemToDelete.mProductId));
  };
  const resetModalState = () => {
    console.log("[ProductsPopup] Resetting modal state");
    setSelectedItems([]); // Reset selected items
    setSearchQuery(""); // Clear search query
    setCurrentPage(0); // Reset to first page
    setShowProductSelection(false); // Reset product selection view
    setSelectedProduct({}); // Clear selected product
    setWeightPopup(false); // Close weight popup if open
    setUomPopup(false);
    setPendingProduct(null);
    setSelectedUomId(null);
    setUomQuantity("1");
    console.log(
      "[ProductsPopup] State after reset: selectedItems=",
      selectedItems,
      "searchQuery=",
      searchQuery,
      "currentPage=",
      currentPage,
      "showProductSelection=",
      showProductSelection
    );
  };

  const handleSubmit = () => {
    const updatedItems = selectedItems.map((item) => ({
      ...item,
      weight: Number(item.weight),
      rawWeight: item.rawWeight || item.weight.toString(),
      unit: item.unit || unit || item?.uomData?.[0]?.name?.toLowerCase() || "kg",
    }));
    onSubmit(updatedItems);
    setVisibleProductsModal(false);
    resetModalState();
  };

  const handleTotalQty = (value) => {
    if (Object.keys(selectedProduct).length === 0 || selectedProduct.isReturn) return;

    let newWeight = selectedProduct.weight ? selectedProduct.weight.toString() : "0";
    const isPredefinedButtonsEnabled = posConfig?.predefined_weight_buttons === "Y";

    let displayWeight = isPredefinedButtonsEnabled && unit === "g" ? (Number(newWeight) * 1000).toString() : newWeight;

    const currentHasDecimal = displayWeight.includes(".");

    switch (value) {
      case "x": // Backspace
        displayWeight = displayWeight.slice(0, -1) || "0";
        setHasDecimal(displayWeight.includes("."));
        break;
      case "clear":
        displayWeight = "0";
        setIsFirstInput(true);
        setHasDecimal(false);
        break;
      case ".":
        if (!currentHasDecimal && displayWeight.length !== "0" && (unit === "kg" || !isPredefinedButtonsEnabled)) {
          displayWeight = `${displayWeight}.`;
          setHasDecimal(true);
        }
        break;
      default:
        if (isFirstInput && displayWeight === "0") {
          displayWeight = value;
          setIsFirstInput(false);
        } else {
          displayWeight = `${displayWeight}${value}`;
        }
        setHasDecimal(displayWeight.includes("."));
        break;
    }

    newWeight = isPredefinedButtonsEnabled && unit === "g" ? (Number(displayWeight) / 1000).toString() : displayWeight;

    setSelectedProduct({
      ...selectedProduct,
      weight: newWeight,
      rawWeight: displayWeight,
      unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
    });
  };

  const handleInputChange = (value) => {
    if (Object.keys(selectedProduct).length === 0) return;

    const isPredefinedButtonsEnabled = posConfig?.predefined_weight_buttons === "Y";
    const isDecimal = selectedProduct?.uomData?.[0]?.decimal === "Y";
    let sanitizedValue = value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
    if (isPredefinedButtonsEnabled && unit === "g" && !isDecimal) {
      sanitizedValue = sanitizedValue.replace(/\./g, "");
    }
    let newWeight = sanitizedValue === "" ? "0" : sanitizedValue;

    if (isPredefinedButtonsEnabled && unit === "g") {
      newWeight = (Number(sanitizedValue) / 1000).toString();
    }

    setSelectedProduct({
      ...selectedProduct,
      weight: newWeight,
      rawWeight: sanitizedValue,
      unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
    });
    setHasDecimal(newWeight.includes("."));
    setIsFirstInput(false);
  };

  const callGetWeightAPI = async (productDefined) => {
    try {
      const response = await Axios({
        url: `${tillData?.tillAccess?.cwrTill?.hardwareController?.imageUrl}getWeight`,
        method: "POST",
      });
      const productToUpdate = productDefined && Object.keys(productDefined)?.length > 0 ? productDefined : selectedProductInCart;
      if (response?.data?.Weight) {
        const weight = parseFloat(response.data.Weight.replace("kg", "").trim());
        if (!isNaN(weight) && weight !== parseFloat(selectedProduct.weight)) {
          productToUpdate.weight = weight;
          productToUpdate.rawWeight = weight.toString();
          productToUpdate.unit = unit || productToUpdate?.uomData?.[0]?.name?.toLowerCase() || "kg";
          setSelectedProductInCart({ ...productToUpdate });
          const updatedProduct = { ...selectedProduct, weight: weight, rawWeight: weight.toString(), unit: unit || productToUpdate?.uomData?.[0]?.name?.toLowerCase() || "kg" };
          setSelectedProduct(updatedProduct);
          setHasDecimal(weight.toString().includes("."));
        }
      }
    } catch (error) {
      console.error("Error in callGetWeightAPI:", error);
    }
  };

  useEffect(() => {
    let interval;
    if (weightPopup) {
      callGetWeightAPI(selectedProduct);
      interval = setInterval(() => {
        callGetWeightAPI(selectedProduct);
      }, 1000);
    }
    return () => {
      clearInterval(interval);
    };
  }, [weightPopup]);

  const frequentWeights = unit === "kg" ? [0.25, 0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0, 2.5, 3.0, 4.0, 5.0] : [250, 500, 750, 1000, 1250, 1500, 1750, 2000, 2500, 3000, 4000, 5000];

  const handleFrequentWeight = (weight) => {
    const weightInKg = unit === "g" ? weight / 1000 : weight;
    setSelectedProduct({
      ...selectedProduct,
      weight: weightInKg.toString(),
      rawWeight: unit === "g" ? weight.toString() : weightInKg.toString(),
      unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
    });
    setHasDecimal(weightInKg.toString().includes("."));
  };

  return (
    <>
      <Modal
        visible={uomPopup}
        closable={true}
        onCancel={handleUomCancel}
        keyboard={false}
        footer={null}
        width="400px"
        centered
        bodyStyle={{ padding: "24px", backgroundColor: "#fff" }}
        title={null}
        closeIcon={<CloseOutlined style={{ fontSize: "14px" }} />}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Header */}
          <div>
            <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 600, color: "#000" }}>Select Unit for {pendingProduct?.name}</h3>
            <p style={{ margin: "4px 0 0 0", fontSize: "14px", color: "#666" }}>Choose whether to sell by pieces or weight</p>
          </div>

          {/* UOM Selection Cards */}
          <div style={{ display: "flex", gap: "12px" }}>
            {availableUoms.map((uom) => (
              <div
                key={uom.csUomId}
                onClick={() => setSelectedUomId(uom.csUomId)}
                style={{
                  flex: 1,
                  padding: "16px",
                  border: selectedUomId === uom.csUomId ? "2px solid #000" : "1px solid #e0e0e0",
                  borderRadius: "8px",
                  cursor: "pointer",
                  backgroundColor: selectedUomId === uom.csUomId ? "#000" : "#fff",
                  color: selectedUomId === uom.csUomId ? "#fff" : "#000",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.2s ease",
                }}
              >
                {getUomIcon(uom.name)}
                <span style={{ fontSize: "14px", fontWeight: 500 }}>{uom.name}</span>
              </div>
            ))}
          </div>

          {/* Quantity Input */}
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#000",
              }}
            >
              Quantity ({selectedUom?.ediCode || selectedUom?.name || "units"})
            </label>
            <Input
              ref={uomInputRef}
              type="text"
              value={uomQuantity}
              onChange={(e) => handleUomInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleUomConfirm();
                }
              }}
              style={{
                width: "100%",
                height: "48px",
                fontSize: "16px",
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
              }}
            />
          </div>

          {/* Total Price */}
          <div
            style={{
              backgroundColor: "#f5f5f5",
              padding: "16px",
              borderRadius: "8px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span style={{ fontSize: "14px", color: "#666" }}>Total Price</span>
            <span style={{ fontSize: "24px", fontWeight: 600, color: "#000" }}>₹{Number(uomTotalPrice).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
          </div>

          {/* Action Buttons */}
          <div style={{ display: "flex", gap: "12px", marginTop: "4px" }}>
            <Button
              onClick={handleUomCancel}
              style={{
                flex: 1,
                height: "48px",
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
                backgroundColor: "#fff",
                color: "#000",
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUomConfirm}
              style={{
                flex: 1,
                height: "48px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "#000",
                color: "#fff",
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              Add to Cart
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        visible={visibleProductsModal}
        centered={true}
        width={"72%"}
        closable={true}
        closeIcon={
          <CloseOutlined
            style={{ color: "#2F3856", fontSize: "16px" }}
            onClick={() => {
              setVisibleProductsModal(false);
              localStorage.removeItem("productSegment");
              resetModalState();
            }}
          />
        }
        footer={null}
      >
        <BarCodeHandler setSelectedItems={setSelectedItems} />
        {showProductSelection ? (
          <div style={{ padding: "5vh 0 0 0", height: "85vh", position: "relative" }}>
            <Input
              placeholder="Search Item Code / Description"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(0);
              }}
              style={{
                borderRadius: "7px",
                padding: "0.8vh 0.8vw",
                border: "1px solid #d9d9d9",
                width: "100%",
              }}
            />

            <Row style={{ padding: "2vh 0" }} gutter={[16, 16]}>
              {paginatedData.length > 0 ? (
                paginatedData.map((item, index) => {
                  const cartItem = selectedItems.find((cartItem) => cartItem.mProductId === item.mProductId);
                  const stdPrecision = item?.uomData?.[0]?.stdprecision ?? 0;
                  const uomName = item?.uomData?.[0]?.name ?? "";
                  const quantity = cartItem ? cartItem.rawWeight || cartItem.weight.toString() : "0";
                  const isDecimal = item?.uomData?.[0]?.decimal === "Y";

                  return (
                    <Col key={index} span={12} style={{ padding: "0 8px" }}>
                      <Row style={{ padding: "1.5%", width: "100%", border: "1px solid #d9d9d9", height: "10vh" }}>
                        <Col span={12}>
                          <p
                            title={item.name}
                            style={{
                              margin: 0,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "100%",
                            }}
                          >
                            {item.name}
                          </p>
                          <p style={{ margin: 0 }}>
                            {item.sunitprice} - {uomName}
                          </p>
                        </Col>
                        <Col span={12} style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <Button
                              onClick={() => handleDecrease(item)}
                              style={{
                                backgroundColor: "#2F3856",
                                color: "#fff",
                                width: "2.5vw",
                                height: "4vh",
                              }}
                              icon={<MinusOutlined />}
                            />
                            <Input
                              type="text"
                              value={quantity}
                              onChange={(e) => handleQuantityChange(item, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  const newValue = e.target.value || "0";
                                  handleQuantityChange(item, newValue);
                                } else if (!isDecimal && !/[\d]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                                  e.preventDefault();
                                } else if (isDecimal && !/[\d.]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              onBlur={(e) => {
                                const newValue = e.target.value || "0";
                                if (isDecimal && [".", "0.", "0.0", "0.00"].includes(newValue)) {
                                  handleQuantityChange(item, "0");
                                } else {
                                  handleQuantityChange(item, newValue);
                                }
                              }}
                              placeholder={isDecimal ? "e.g., 0.001" : "e.g., 1"}
                              style={{
                                width: "60px",
                                height: "4vh",
                                textAlign: "center",
                              }}
                            />
                            <Button
                              onClick={() => handleIncrease(item)}
                              style={{
                                backgroundColor: "#2F3856",
                                color: "#fff",
                                width: "2.5vw",
                                height: "4vh",
                              }}
                              icon={<PlusOutlined />}
                            />
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  );
                })
              ) : (
                <p style={{ textAlign: "center", width: "100%" }}>No products found</p>
              )}
            </Row>

            <div style={{ position: "absolute", bottom: "0vh", width: "100%", padding: "1vh 0" }}>
              <Row align="middle" justify="space-between">
                <Col>
                  <Button
                    disabled={currentPage === 0}
                    onClick={handlePreviousPage}
                    style={{
                      textAlign: "center",
                      backgroundColor: currentPage === 0 ? "#ccc" : "#2F3856",
                      width: "4vw",
                      height: "6.5vh",
                      color: "#fff",
                      borderRadius: "5px",
                      marginRight: "1vw",
                    }}
                  >
                    {"<"}
                  </Button>
                  <span style={{ fontWeight: "bold", margin: "0 1vw" }}>
                    Page {currentPage + 1} of {totalPages || 1}
                  </span>
                  <Button
                    disabled={currentPage >= totalPages - 1}
                    onClick={handleNextPage}
                    style={{
                      textAlign: "center",
                      backgroundColor: currentPage >= totalPages - 1 ? "#ccc" : "#2F3856",
                      width: "4vw",
                      height: "6.5vh",
                      color: "#fff",
                      borderRadius: "5px",
                      marginLeft: "1vw",
                    }}
                  >
                    {">"}
                  </Button>
                </Col>
                <Col>
                  <Button
                    onClick={() => setShowProductSelection(false)}
                    style={{
                      backgroundColor: "#2F3856",
                      color: "#fff",
                      width: "8vw",
                      height: "6.5vh",
                      borderRadius: "5px",
                      marginRight: "1vw",
                    }}
                    disabled={selectedItems.length === 0}
                  >
                    Okay
                  </Button>
                </Col>
              </Row>
            </div>
          </div>
        ) : (
          <div style={{ height: "81vh", position: "relative" }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: "2vh", marginTop: "3vh" }}>
              <Col>
                <h3>{selectedProduct?.name || ""}</h3>
              </Col>
              <Col>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowProductSelection(true)} style={{ backgroundColor: "#2F3856", borderColor: "#2F3856" }}>
                  Add Products
                </Button>
              </Col>
            </Row>
            <Row
              style={{
                padding: "0.1rem 0.75rem",
                fontSize: "0.75rem",
                fontWeight: 600,
                color: "#2F3856",
                borderBottom: "1px solid #e8e8e8",
                marginBottom: "0.5rem",
              }}
            >
              <Col span={6}>Name</Col>
              <Col span={4} style={{ textAlign: "right" }}>
                Unit Price
              </Col>
              <Col span={5} style={{ textAlign: "center" }}>
                Qty
              </Col>
              <Col span={2} style={{ textAlign: "right" }}>
                Total Qty
              </Col>
              <Col span={5} style={{ textAlign: "center" }}>
                Total Price
              </Col>
              <Col span={2}></Col>
            </Row>
            <Scrollbars style={{ height: "60vh" }}>
              {selectedItems.map((item) => {
                const unitPrice = parseFloat(item.sunitprice) || 0;
                const quantity = parseFloat(item.weight * (selectedProduct.weight || 1)) || 0;
                const totalPrice = unitPrice * quantity;
                const isDecimal = item?.uomData?.[0]?.decimal === "Y";
                return (
                  <Row
                    key={item.id || item.name}
                    style={{
                      padding: "0.5rem 0.9rem",
                      width: "100%",
                      marginBottom: "0.4rem",
                      height: "auto",
                      alignItems: "center",
                    }}
                  >
                    <Col span={6}>
                      <p
                        title={item.name}
                        style={{
                          margin: 0,
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          color: "#333",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "100%",
                        }}
                      >
                        {item.name}
                      </p>
                    </Col>
                    <Col span={4} style={{ textAlign: "right" }}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "0.8rem",
                          color: "#2F3856",
                        }}
                      >
                        {unitPrice.toLocaleString("en-IN")}
                      </p>
                    </Col>
                    <Col span={5} style={{ textAlign: "center" }}>
                      <Input
                        type="text"
                        value={item.rawWeight || item.weight}
                        onChange={(e) => handleQuantityChange(item, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const newValue = e.target.value || "0";
                            handleQuantityChange(item, newValue);
                          } else if (!isDecimal && !/[\d]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                            e.preventDefault();
                          } else if (isDecimal && !/[\d.]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onBlur={(e) => {
                          const newValue = e.target.value || "0";
                          if (isDecimal && [".", "0.", "0.0", "0.00"].includes(newValue)) {
                            handleQuantityChange(item, "0");
                          } else {
                            handleQuantityChange(item, newValue);
                          }
                        }}
                        placeholder={isDecimal ? "e.g., 0.001" : "e.g., 1"}
                        style={{
                          width: "60px",
                          height: "25px",
                          textAlign: "center",
                          fontSize: "0.8rem",
                        }}
                      />
                    </Col>
                    <Col span={2} style={{ textAlign: "right" }}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "0.8rem",
                          color: "#2F3856",
                        }}
                      >
                        {quantity}
                      </p>
                    </Col>
                    <Col span={5} style={{ textAlign: "center" }}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "0.8rem",
                          color: "#2F3856",
                        }}
                      >
                        {totalPrice.toLocaleString("en-IN")}
                      </p>
                    </Col>
                    <Col span={2} style={{ textAlign: "center" }}>
                      <img
                        src={DeleteIcon}
                        style={{
                          height: "2.5vh",
                          cursor: "pointer",
                        }}
                        onClick={() => handleDelete(item)}
                      />
                    </Col>
                  </Row>
                );
              })}
            </Scrollbars>
            <Button
              style={{
                position: "absolute",
                bottom: "0.5rem",
                right: "0.5rem",
                backgroundColor: "#2F3856",
                color: "#fff",
                width: "7vw",
                height: "4.5vh",
                borderRadius: "3px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "none",
                fontSize: "0.85rem",
              }}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </div>
        )}
      </Modal>

      {posConfig?.predefined_weight_buttons === "Y" ? (
        <Modal visible={weightPopup} closable={false} keyboard={false} footer={null} width="50%" centered bodyStyle={{ padding: 10, backgroundColor: "#F3F4F9", height: "80vh" }}>
          <Row>
            <Col span={24} style={{ paddingBottom: "1vh" }}>
              <img src={selectedProduct.imageurl} style={{ height: "4.5vh", paddingRight: "1vh" }} />
              <span style={{ fontWeight: 500, fontSize: "1.2rem" }}>{selectedProduct.name}</span>
            </Col>
            <Col span={12} style={{ textAlign: "left", paddingLeft: "0.5vw" }}>
              <span style={{ fontWeight: 500, fontSize: "1rem", marginLeft: "0.3vw" }}>Sale Price: {selectedProduct.sunitprice}</span>
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              <img src={Refresh} style={{ cursor: "pointer" }} onClick={() => callGetWeightAPI(selectedProduct)} />
            </Col>
          </Row>

          <Row style={{ padding: "1vh 3.5% 0 3.5%" }} gutter={[16, 16]}>
            <Col span={16}>
              <Row gutter={[8, 6]}>
                <Col span={24} style={{ paddingBottom: "1vh" }}>
                  <Input
                    className="productSearch sm-payment-search totalQtyInput"
                    style={{
                      borderRadius: "5px",
                      height: "8.3vh",
                      textAlign: "right",
                      fontWeight: "500",
                      fontSize: "2.5em",
                      borderColor: "#fff",
                      color: "#2F3856",
                      fontFamily: "Inter",
                    }}
                    value={
                      selectedProduct.rawWeight
                        ? posConfig?.predefined_weight_buttons === "Y" && unit === "g"
                          ? `${selectedProduct.rawWeight} ${unit}`
                          : `${selectedProduct.rawWeight} ${unit}`
                        : ""
                    }
                    id="sm-weight-popup-input"
                    ref={weightInputRef}
                    onChange={(e) => handleInputChange(e.target.value.replace(/[^0-9.]/g, ""))}
                    readOnly={Object.keys(selectedProduct).length === 0}
                    placeholder="Enter Quantity"
                    onPressEnter={() => {
                      const updatedProduct = {
                        ...selectedProduct,
                        weight: Number(selectedProduct.weight) === 0 ? "1" : selectedProduct.weight,
                        rawWeight: selectedProduct.rawWeight || selectedProduct.weight.toString(),
                        unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
                      };
                      setSelectedProduct(updatedProduct);
                      setWeightPopup(false);
                      selectedProductCallback(updatedProduct);
                    }}
                    onKeyDown={(e) => {
                      const isDecimal = selectedProduct?.uomData?.[0]?.decimal === "Y";
                      const currentValue = e.target.value.replace(/[^0-9.]/g, "");
                      if (e.key === "Enter") {
                        e.stopPropagation();
                        e.preventDefault();
                        const updatedProduct = {
                          ...selectedProduct,
                          weight: Number(selectedProduct.weight) === 0 ? "1" : selectedProduct.weight,
                          rawWeight: selectedProduct.rawWeight || selectedProduct.weight.toString(),
                          unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
                        };
                        setSelectedProduct(updatedProduct);
                        setWeightPopup(false);
                        selectedProductCallback(updatedProduct);
                      } else if (e.key === "Backspace") {
                        e.preventDefault();
                        handleTotalQty("x");
                      } else if (!isDecimal && !/[\d]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                        e.preventDefault();
                      } else if (isDecimal && !/[\d.]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Col>
                <Col span={6}>
                  <button
                    id="sm-amount-button1"
                    className="amt-dial-btn-production"
                    onClick={() => handleTotalQty("1")}
                    style={{
                      width: "100%",
                      height: "10vh",
                      border: "1px solid #d9d9d9",
                      borderRadius: "5px",
                      fontSize: "1.2rem",
                    }}
                  >
                    1
                  </button>
                </Col>
                <Col span={6}>
                  <button
                    id="sm-amount-button2"
                    className="amt-dial-btn-production"
                    onClick={() => handleTotalQty("2")}
                    style={{
                      width: "100%",
                      height: "10vh",
                      border: "1px solid #d9d9d9",
                      borderRadius: "5px",
                      fontSize: "1.2rem",
                    }}
                  >
                    2
                  </button>
                </Col>
                <Col span={6}>
                  <button
                    id="sm-amount-button3"
                    className="amt-dial-btn-production"
                    onClick={() => handleTotalQty("3")}
                    style={{
                      width: "100%",
                      height: "10vh",
                      border: "1px solid #d9d9d9",
                      borderRadius: "5px",
                      fontSize: "1.2rem",
                    }}
                  >
                    3
                  </button>
                </Col>
                <Col span={6}>
                  <button
                    className="amt-dial-btn-production"
                    id="sm-amount-buttonRemove"
                    onClick={() => handleTotalQty("x")}
                    style={{
                      width: "100%",
                      height: "10vh",
                      border: "1px solid #d9d9d9",
                      borderRadius: "5px",
                    }}
                  >
                    <img src={CancelArrow} alt="back space" width={25} height={15} />
                  </button>
                </Col>
                <Col span={6}>
                  <button
                    id="sm-amount-button4"
                    className="amt-dial-btn-production"
                    onClick={() => handleTotalQty("4")}
                    style={{
                      width: "100%",
                      height: "10vh",
                      border: "1px solid #d9d9d9",
                      borderRadius: "5px",
                      fontSize: "1.2rem",
                    }}
                  >
                    4
                  </button>
                </Col>
                <Col span={6}>
                  <button
                    id="sm-amount-button5"
                    className="amt-dial-btn-production"
                    onClick={() => handleTotalQty("5")}
                    style={{
                      width: "100%",
                      height: "10vh",
                      border: "1px solid #d9d9d9",
                      borderRadius: "5px",
                      fontSize: "1.2rem",
                    }}
                  >
                    5
                  </button>
                </Col>
                <Col span={6}>
                  <button
                    id="sm-amount-button6"
                    className="amt-dial-btn-production"
                    onClick={() => handleTotalQty("6")}
                    style={{
                      width: "100%",
                      height: "10vh",
                      border: "1px solid #d9d9d9",
                      borderRadius: "5px",
                      fontSize: "1.2rem",
                    }}
                  >
                    6
                  </button>
                </Col>
                <Col span={6}>
                  <button
                    id="sm-amount-buttonClear"
                    className="amt-dial-btn-production"
                    onClick={() => handleTotalQty("clear")}
                    style={{
                      width: "100%",
                      height: "10vh",
                      border: "1px solid #d9d9d9",
                      borderRadius: "5px",
                      fontSize: "1rem",
                    }}
                  >
                    Clear
                  </button>
                </Col>
                <Col span={18}>
                  <Row gutter={[8, 6]}>
                    <Col span={8}>
                      <button
                        id="sm-amount-button7"
                        className="amt-dial-btn-production"
                        onClick={() => handleTotalQty("7")}
                        style={{
                          width: "100%",
                          height: "10vh",
                          border: "1px solid #d9d9d9",
                          borderRadius: "5px",
                          fontSize: "1.2rem",
                        }}
                      >
                        7
                      </button>
                    </Col>
                    <Col span={8}>
                      <button
                        id="sm-amount-button8"
                        className="amt-dial-btn-production"
                        onClick={() => handleTotalQty("8")}
                        style={{
                          width: "100%",
                          height: "10vh",
                          border: "1px solid #d9d9d9",
                          borderRadius: "5px",
                          fontSize: "1.2rem",
                        }}
                      >
                        8
                      </button>
                    </Col>
                    <Col span={8}>
                      <button
                        id="sm-amount-button9"
                        className="amt-dial-btn-production"
                        onClick={() => handleTotalQty("9")}
                        style={{
                          width: "100%",
                          height: "10vh",
                          border: "1px solid #d9d9d9",
                          borderRadius: "5px",
                          fontSize: "1.2rem",
                        }}
                      >
                        9
                      </button>
                    </Col>
                    <Col span={8}>
                      <button
                        id="sm-amount-button0"
                        className="amt-dial-btn-production"
                        onClick={() => handleTotalQty("0")}
                        style={{
                          width: "100%",
                          height: "10vh",
                          border: "1px solid #d9d9d9",
                          borderRadius: "5px",
                          fontSize: "1.2rem",
                        }}
                      >
                        0
                      </button>
                    </Col>
                    <Col span={8}>
                      <button
                        id="sm-amount-button00"
                        className="amt-dial-btn-production"
                        onClick={() => handleTotalQty("00")}
                        style={{
                          width: "100%",
                          height: "10vh",
                          border: "1px solid #d9d9d9",
                          borderRadius: "5px",
                          fontSize: "1rem",
                        }}
                      >
                        00
                      </button>
                    </Col>
                    <Col span={8}>
                      <button
                        id="sm-amount-button."
                        className="amt-dial-btn-production"
                        onClick={() => handleTotalQty(".")}
                        style={{
                          width: "100%",
                          height: "10vh",
                          border: "1px solid #d9d9d9",
                          borderRadius: "5px",
                          fontSize: "1.2rem",
                        }}
                      >
                        .
                      </button>
                    </Col>
                  </Row>
                </Col>
                <Col span={6}>
                  <button
                    id="sm-amount-buttonEnter"
                    className="amt-dial-btn-production-ent"
                    onClick={() => {
                      const updatedProduct = {
                        ...selectedProduct,
                        weight: Number(selectedProduct.weight) === 0 ? "1" : selectedProduct.weight,
                        rawWeight: selectedProduct.rawWeight || selectedProduct.weight.toString(),
                        unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
                      };
                      setSelectedProduct(updatedProduct);
                      setWeightPopup(false);
                      selectedProductCallback(updatedProduct);
                    }}
                    style={{
                      width: "100%",
                      height: "20.5vh",
                      backgroundColor: "#2F3856",
                      color: "#fff",
                      borderRadius: "5px",
                      fontSize: "1.2rem",
                    }}
                  >
                    Enter
                  </button>
                </Col>
              </Row>
            </Col>
            <Col span={8}>
              <Row gutter={[8, 8]}>
                <Col span={24}>
                  <div
                    style={{
                      backgroundColor: "#fff",
                      padding: "1vh",
                      borderRadius: "5px",
                      border: "1px solid #d9d9d9",
                    }}
                  >
                    <h4 style={{ fontSize: "0.9rem", fontWeight: 600, margin: "0 0 0.5vh 0" }}>Unit</h4>
                    <Button
                      style={{
                        width: "100%",
                        height: "5vh",
                        marginBottom: "0.5vh",
                        backgroundColor: unit === "kg" ? "#2F3856" : "#fff",
                        color: unit === "kg" ? "#fff" : "#2F3856",
                        border: "1px solid #d9d9d9",
                      }}
                      onClick={() => {
                        setUnit("kg");
                        handleTotalQty("clear");
                      }}
                    >
                      Kilograms
                    </Button>
                    <Button
                      style={{
                        width: "100%",
                        height: "5vh",
                        marginBottom: "0.5vh",
                        backgroundColor: unit === "g" ? "#2F3856" : "#fff",
                        color: unit === "g" ? "#fff" : "#2F3856",
                        border: "1px solid #d9d9d9",
                      }}
                      onClick={() => {
                        setUnit("g");
                        handleTotalQty("clear");
                      }}
                    >
                      Grams
                    </Button>
                  </div>
                </Col>
                <Col span={24}>
                  <div
                    style={{
                      backgroundColor: "#fff",
                      padding: "1vh",
                      borderRadius: "5px",
                      border: "1px solid #d9d9d9",
                      maxHeight: "36vh",
                      overflowY: "auto",
                    }}
                  >
                    <h4 style={{ fontSize: "0.9rem", fontWeight: 600, margin: "0 0 0.5vh 0" }}>Quick Select</h4>
                    <Row gutter={[8, 8]}>
                      {frequentWeights.map((weight) => (
                        <Col span={12} key={weight}>
                          <Button
                            style={{
                              width: "100%",
                              height: "4vh",
                              fontSize: "0.85rem",
                              border: "1px solid #d9d9d9",
                            }}
                            onClick={() => handleFrequentWeight(weight)}
                          >
                            {weight} {unit}
                          </Button>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>

          <Row style={{ padding: "3% 3.5% 0 3.5%" }} gutter={[8, 6]}>
            <Col span={12}>
              <Button
                onClick={() => {
                  selectedProductCallback(null);
                  setWeightPopup(false);
                }}
                style={{ width: "100%", height: "7vh", borderRadius: "5px", backgroundColor: "#fff", color: "#2F3856" }}
              >
                Cancel
              </Button>
            </Col>
            <Col span={12}>
              <Button
                onClick={() => {
                  const updatedProduct = {
                    ...selectedProduct,
                    weight: Number(selectedProduct.weight) === 0 ? "1" : selectedProduct.weight,
                    rawWeight: selectedProduct.rawWeight || selectedProduct.weight.toString(),
                    unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
                  };
                  setSelectedProduct(updatedProduct);
                  setWeightPopup(false);
                  selectedProductCallback(updatedProduct);
                }}
                style={{ width: "100%", height: "7vh", borderRadius: "5px", backgroundColor: "#2F3856", color: "#fff" }}
              >
                Confirm
              </Button>
            </Col>
          </Row>
        </Modal>
      ) : (
        <Modal visible={weightPopup} closable={false} keyboard={false} footer={null} width="30%" centered bodyStyle={{ padding: 10, backgroundColor: "#F3F4F9" }}>
          <Row>
            <Col span={12} style={{ textAlign: "left", paddingLeft: "0.5vw" }}></Col>
            <Col span={12} style={{ textAlign: "right" }}>
              <img
                src={Refresh}
                style={{ cursor: "pointer" }}
                onClick={() => {
                  callGetWeightAPI(selectedProduct);
                }}
              />
            </Col>
          </Row>

          <Row style={{ padding: "0 3.5% 0 3.5%" }} gutter={[8, 6]}>
            <Col span={24} style={{ paddingBottom: "1vh" }}>
              <img src={selectedProduct.imageurl} style={{ height: "4.5vh" }} /> <span style={{ fontWeight: 500, fontSize: "1rem" }}>{selectedProduct.name}</span>
            </Col>
            <Col span={24} style={{ paddingBottom: "1vh" }}>
              Sale Price: <span style={{ fontWeight: 500, fontSize: "0.9rem" }}>{selectedProduct.sunitprice}</span>
            </Col>
          </Row>

          <Row style={{ padding: "0 3.5% 0 3.5%" }} gutter={[8, 6]}>
            <Col span={24} style={{ paddingBottom: "2vh" }}>
              <Input
                className="productSearch sm-payment-search totalQtyInput"
                style={{
                  borderRadius: "0.5vw",
                  height: "8.3vh",
                  textAlign: "right",
                  fontWeight: "500",
                  fontSize: "2.5em",
                  borderColor: "#fff",
                  color: "#2F3856",
                  fontFamily: "Inter",
                }}
                value={selectedProduct.rawWeight || selectedProduct.weight || ""}
                id="sm-weight-popup-input"
                ref={weightInputRef}
                onChange={(e) => handleInputChange(e.target.value)}
                readOnly={Object.keys(selectedProduct).length === 0}
                placeholder="Enter Quantity"
                onPressEnter={() => {
                  const updatedProduct = {
                    ...selectedProduct,
                    weight: Number(selectedProduct.weight) === 0 ? "1" : selectedProduct.weight,
                    rawWeight: selectedProduct.rawWeight || selectedProduct.weight.toString(),
                    unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
                  };
                  setSelectedProduct(updatedProduct);
                  setWeightPopup(false);
                  selectedProductCallback(updatedProduct);
                }}
                onKeyDown={(e) => {
                  const isDecimal = selectedProduct?.uomData?.[0]?.decimal === "Y";
                  if (e.key === "Enter") {
                    e.stopPropagation();
                    e.preventDefault();
                    const updatedProduct = {
                      ...selectedProduct,
                      weight: Number(selectedProduct.weight) === 0 ? "1" : selectedProduct.weight,
                      rawWeight: selectedProduct.rawWeight || selectedProduct.weight.toString(),
                      unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
                    };
                    setSelectedProduct(updatedProduct);
                    setWeightPopup(false);
                    selectedProductCallback(updatedProduct);
                  } else if (e.key === "Backspace") {
                    e.preventDefault();
                    handleTotalQty("x");
                  } else if (!isDecimal && !/[\d]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                    e.preventDefault();
                  } else if (isDecimal && !/[\d.]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </Col>
            <Col span={6}>
              <button id="sm-amount-button1" className="amt-dial-btn-production" onClick={() => handleTotalQty("1")}>
                1
              </button>
            </Col>
            <Col span={6}>
              <button id="sm-amount-button2" className="amt-dial-btn-production" onClick={() => handleTotalQty("2")}>
                2
              </button>
            </Col>
            <Col span={6}>
              <button id="sm-amount-button3" className="amt-dial-btn-production" onClick={() => handleTotalQty("3")}>
                3
              </button>
            </Col>
            <Col span={6}>
              <button className="amt-dial-btn-production" id="sm-amount-buttonRemove" onClick={() => handleTotalQty("x")}>
                <img src={CancelArrow} alt="back space" width={25} height={15} />
              </button>
            </Col>
            <Col span={6}>
              <button id="sm-amount-button4" className="amt-dial-btn-production" onClick={() => handleTotalQty("4")}>
                4
              </button>
            </Col>
            <Col span={6}>
              <button id="sm-amount-button5" className="amt-dial-btn-production" onClick={() => handleTotalQty("5")}>
                5
              </button>
            </Col>
            <Col span={6}>
              <button id="sm-amount-button6" className="amt-dial-btn-production" onClick={() => handleTotalQty("6")}>
                6
              </button>
            </Col>
            <Col span={6}>
              <button id="sm-amount-buttonClear" className="amt-dial-btn-production" onClick={() => handleTotalQty("clear")}>
                Clear
              </button>
            </Col>
            <Col span={18}>
              <Row gutter={[8, 6]}>
                <Col span={8}>
                  <button id="sm-amount-button7" className="amt-dial-btn-production" onClick={() => handleTotalQty("7")}>
                    7
                  </button>
                </Col>
                <Col span={8}>
                  <button id="sm-amount-button8" className="amt-dial-btn-production" onClick={() => handleTotalQty("8")}>
                    8
                  </button>
                </Col>
                <Col span={8}>
                  <button id="sm-amount-button9" className="amt-dial-btn-production" onClick={() => handleTotalQty("9")}>
                    9
                  </button>
                </Col>
                <Col span={8}>
                  <button id="sm-amount-button0" className="amt-dial-btn-production" onClick={() => handleTotalQty("0")}>
                    0
                  </button>
                </Col>
                <Col span={8}>
                  <button id="sm-amount-button00" className="amt-dial-btn-production" onClick={() => handleTotalQty("00")}>
                    00
                  </button>
                </Col>
                <Col span={8}>
                  <button id="sm-amount-button." className="amt-dial-btn-production" onClick={() => handleTotalQty(".")}>
                    .
                  </button>
                </Col>
              </Row>
            </Col>
            <Col span={6}>
              <button
                id="sm-amount-buttonEnter"
                className="amt-dial-btn-production-ent"
                onClick={() => {
                  const updatedProduct = {
                    ...selectedProduct,
                    weight: Number(selectedProduct.weight) === 0 ? "1" : selectedProduct.weight,
                    rawWeight: selectedProduct.rawWeight || selectedProduct.weight.toString(),
                    unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
                  };
                  setSelectedProduct(updatedProduct);
                  setWeightPopup(false);
                  selectedProductCallback(updatedProduct);
                }}
              >
                Enter
              </button>
            </Col>
          </Row>

          <Row style={{ padding: "3% 3.5% 0 3.5%" }} gutter={[8, 6]}>
            <Col span={12}>
              <Button
                onClick={() => {
                  selectedProductCallback(null);
                  setWeightPopup(false);
                }}
                style={{ width: "100%", height: "7vh", borderRadius: "5px", backgroundColor: "#fff", color: "#2F3856" }}
              >
                Cancel
              </Button>
            </Col>
            <Col span={12}>
              <Button
                onClick={() => {
                  const updatedProduct = {
                    ...selectedProduct,
                    weight: Number(selectedProduct.weight) === 0 ? "1" : selectedProduct.weight,
                    rawWeight: selectedProduct.rawWeight || selectedProduct.weight.toString(),
                    unit: unit || selectedProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
                  };
                  setSelectedProduct(updatedProduct);
                  setWeightPopup(false);
                  selectedProductCallback(updatedProduct);
                }}
                style={{ width: "100%", height: "7vh", borderRadius: "5px", backgroundColor: "#2F3856", color: "#fff" }}
              >
                Confirm
              </Button>
            </Col>
          </Row>
        </Modal>
      )}
    </>
  );
};

export default ProductsPopup;
